import React, {useMemo, useState, useCallback, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {motion, AnimatePresence} from "framer-motion";
import {FIXTURE_STATE_CONFIG} from "@/utils/constants.js";
import {APP_STEPS} from "@/hooks/contest/contestSteps.js";
import {generatePseudo} from "@/utils/pseudoGenerator.js";
import {makeMockFootballLeaderboardBackend, MOCK_ODDS_LIST} from "@/mocks/mockFootballLeaderboard.js";
import {getSmartView} from "@/utils/leaderboardEngine.js";
import {mapBackendToFrontend} from "@/utils/mappers.js";
import {StepBettingFootball} from "@/features/contest/StepBettingFootball.jsx";
import {StepBettingHalfTime} from "@/features/contest/StepBettingHalfTime.jsx";
import {LeaderboardTabs} from "@/features/contest/LeaderboardTabs.jsx";
import {HalfTimeUpsellModal} from "@/features/contest/HalfTimeUpsellModal.jsx";
import {QuizUpsellModal} from "@/features/contest/QuizUpsellModal.jsx";
import HalfTimeRewardOverlay from "@/components/overlays/HalfTimeRewardOverlay.jsx";
import QuizzGame from "@/features/quiz/QuizzGame.jsx";
import {MOCK_QUIZ} from "@/mocks/mockQuiz.js";
import {computeQuizPoints} from "@/utils/quizScoring.js";
import {StepResult} from "@/features/contest/StepResult.jsx";
import {PenaltyShootout} from "@/features/contest/PenaltyShootout.jsx";
import {GameLayout} from "@/features/contest/GameLayout.jsx";
import {StepForm} from "@/features/contest/StepForm.jsx";
import {StepWelcomePenalty} from "@/features/contest/StepWelcomePenalty.jsx";
import {StepPenaltyResult} from "@/features/contest/StepPenaltyResult.jsx";
import QrCodeOverlay from "@/components/overlays/QrCodeOverlay.jsx";
import GoogleReviewOverlay from "@/components/overlays/GoogleReviewOverlay.jsx";
import ModalPlayerDetail from "@/components/ModalPlayerDetail.jsx";
import {Footer} from "@/components/Footer.jsx";
import {calculatePotentialGains} from "@/utils/logic.js";
import {REWARD_TYPE} from "@/hooks/contest/contestStatus.js";

const DEMO_FIXTURES = {
    fr: {
        home: {shortName: "FRA", name: "France", logoUrl: "https://flagcdn.com/w160/fr.png", code: "fr", score: 1},
        away: {shortName: "ARG", name: "Argentine", logoUrl: "https://flagcdn.com/w160/ar.png", code: "ar", score: 0}
    },
    en: {
        home: {
            shortName: "ENG",
            name: "England",
            logoUrl: "https://flagcdn.com/w160/gb-eng.png",
            code: "gb-eng",
            score: 1
        },
        away: {shortName: "ITA", name: "Italy", logoUrl: "https://flagcdn.com/w160/it.png", code: "it", score: 0}
    },
    es: {
        home: {shortName: "ESP", name: "España", logoUrl: "https://flagcdn.com/w160/es.png", code: "es", score: 1},
        away: {
            shortName: "ENG",
            name: "Inglaterra",
            logoUrl: "https://flagcdn.com/w160/gb-eng.png",
            code: "gb-eng",
            score: 0
        }
    },
    it: {
        home: {shortName: "ITA", name: "Italia", logoUrl: "https://flagcdn.com/w160/it.png", code: "it", score: 1},
        away: {shortName: "FRA", name: "Francia", logoUrl: "https://flagcdn.com/w160/fr.png", code: "fr", score: 0}
    },
    pt: {
        home: {shortName: "POR", name: "Portugal", logoUrl: "https://flagcdn.com/w160/pt.png", code: "pt", score: 1},
        away: {shortName: "ESP", name: "Espanha", logoUrl: "https://flagcdn.com/w160/es.png", code: "es", score: 0}
    },
    de: {
        home: {shortName: "GER", name: "Deutschland", logoUrl: "https://flagcdn.com/w160/de.png", code: "de", score: 1},
        away: {shortName: "ARG", name: "Argentinien", logoUrl: "https://flagcdn.com/w160/ar.png", code: "ar", score: 0}
    }
};

const DEMO_REWARDS = {
    fr: {
        rewardLabel: "Une boisson offerte",
        winnerReward: {title: "Pinte, Vin ou Soft", type: REWARD_TYPE.PHYSICAL, code: 'WIN-DRINK'},
        loserReward: {title: "1 Shot de consolation", type: REWARD_TYPE.PHYSICAL, code: 'LOSE-SHOT'}
    },
    en: {
        rewardLabel: "A Free Drink",
        winnerReward: {title: "Pint, Wine or Soft", type: REWARD_TYPE.PHYSICAL, code: 'WIN-DRINK'},
        loserReward: {title: "1 Consolation Shot", type: REWARD_TYPE.PHYSICAL, code: 'LOSE-SHOT'}
    },
    es: {
        rewardLabel: "Una bebida gratis",
        winnerReward: {title: "Caña, Vino o Refresco", type: REWARD_TYPE.PHYSICAL, code: 'WIN-DRINK'},
        loserReward: {title: "1 Chupito gratis", type: REWARD_TYPE.PHYSICAL, code: 'LOSE-SHOT'}
    },
    it: {
        rewardLabel: "Una bevanda offerta",
        winnerReward: {title: "Birra, Vino o Bibita", type: REWARD_TYPE.PHYSICAL, code: 'WIN-DRINK'},
        loserReward: {title: "1 Shot di consolazione", type: REWARD_TYPE.PHYSICAL, code: 'LOSE-SHOT'}
    },
    pt: {
        rewardLabel: "Uma bebida grátis",
        winnerReward: {title: "Cerveja, Vinho ou Refrigerante", type: REWARD_TYPE.PHYSICAL, code: 'WIN-DRINK'},
        loserReward: {title: "1 Shot grátis", type: REWARD_TYPE.PHYSICAL, code: 'LOSE-SHOT'}
    },
    de: {
        rewardLabel: "Ein Gratis-Getränk",
        winnerReward: {title: "Bier, Wein oder Softdrink", type: REWARD_TYPE.PHYSICAL, code: 'WIN-DRINK'},
        loserReward: {title: "1 Trost-Shot", type: REWARD_TYPE.PHYSICAL, code: 'LOSE-SHOT'}
    }
};

const BASE_CONTEST_DATA = {
    fixture: {
        type: "FOOTBALL",
        id: 5,
        startingAt: "2025-12-13 15:00:00",
        label: "Finale",
        state: FIXTURE_STATE_CONFIG.FIRST_HALF,
        minutes: 35,
        extraMinutes: null,
        league: {name: "DEMO CUP", logoUrl: null},
    },
    participantUi: {
        rewardLabel: "Une boisson offerte"
    },
    winnerReward: {title: "Pinte, Vin ou Soft", type: REWARD_TYPE.PHYSICAL, code: 'WIN-DRINK'},
    loserReward: {title: "1 Shot de consolation", type: REWARD_TYPE.PHYSICAL, code: 'LOSE-SHOT'},

    coverUrl: "https://images.gojokodo.com/lasphalte-cover.jpg",
    place: {
        id: 1,
        name: "Spot Burger Joint",
        address: "Halles Biltoki, 1 Rue Rouget de Lisle, 92130 Issy-les-Moulineaux, France",
        googlePlaceId: "ChIJJSXu4qx75kcRrkLEfNkp_60"
    }
};

const randomPseudo = generatePseudo();

// Sous-étape locale à la démo pour la saisie mi-temps (hors APP_STEPS partagé).
// L'upsell est une modale (htUpsellOpen) affichée PAR-DESSUS le classement, pas une étape à part.
const DEMO_HT_STEPS = {BETTING: 'HT_BETTING'};

// Lot mi-temps fictif (distinct du lot final). `status` pour le bandeau, le reste pour l'overlay QR.
const DEMO_HALFTIME_REWARD = {
    status: 'AVAILABLE',
    title: 'Un café ou soft offert',
    type: REWARD_TYPE.PHYSICAL,
    code: 'DEMO-HT-DRINK'
};

// --- PARCOURS TIRS AU BUT (démo) ---
// Sous-étape locale (hors APP_STEPS partagé) : le mode pénos est un plein écran réactif.
const DEMO_PENALTY_STEP = 'PENALTY';

// --- TUNNEL ONBOARDING LATECOMER (démo) : un nouveau venu qui arrive PENDANT la séance.
// welcome pénos → form (pseudo/email, sans prono) → on entre dans la séance.
const DEMO_ONBOARD_WELCOME = 'PEN_ONBOARD_WELCOME';
const DEMO_ONBOARD_FORM = 'PEN_ONBOARD_FORM';
// Résultat dédié péno-only (latecomer) : écran win/lose centré tirs au but (pas le StepResult FT).
const DEMO_PENALTY_RESULT = 'PEN_RESULT';

// Lot pénos fictif (3e lot, distinct du final et de la mi-temps).
const DEMO_PENALTY_REWARD = {
    status: 'AVAILABLE',
    title: 'Un cocktail maison offert',
    type: REWARD_TYPE.PHYSICAL,
    code: 'DEMO-PEN-COCKTAIL'
};

// Séquence scriptée de la séance (simule le "feed" Sportmonks tir-par-tir).
// HOME = équipe à domicile de la fixture démo, AWAY = l'adversaire. Fin dramatique : HOME qualifié.
const DEMO_PENALTY_SCRIPT = [
    {taker: 'HOME', result: 'GOAL'},
    {taker: 'AWAY', result: 'GOAL'},
    {taker: 'HOME', result: 'MISS'},
    {taker: 'AWAY', result: 'MISS'},
    {taker: 'HOME', result: 'GOAL'},
];

const PEN_WINDOW_MS = 6000;   // fenêtre de prono par tir (anneau de décompte)
const PEN_REVEAL_MS = 2100;   // temps d'affichage du résultat avant le tir suivant
const PEN_TAP_REVEAL_MS = 950; // délai après le tap avant de dévoiler (suspense)

// Construit un classement mi-temps figé. `withMe` rejoue le cas "j'ai gagné le lot à la pause"
// (rang 1 garanti pour la démo) ; sinon le joueur n'apparaît pas (cas "je n'ai pas joué la mi-temps").
const buildHalfTimeBoard = ({withMe, prediction, pseudo}) => {
    const board = makeMockFootballLeaderboardBackend(5, {
        meIndex: withMe ? 0 : -1,
        startId: 60,
        targetScore: {home: prediction?.team1 ?? 1, away: prediction?.team2 ?? 0}
    });

    if (withMe) {
        const me = board.find(p => p.isMe);
        if (me) {
            me.pseudo = pseudo;
            me.predictionHome = prediction.team1;
            me.predictionAway = prediction.team2;
            me.totalPoints = 9999; // garantit le rang 1 (gagnant mi-temps) pour la démo
        }
    } else {
        board.forEach(p => { p.isMe = false; });
    }

    board.sort((a, b) => b.totalPoints - a.totalPoints);
    board.forEach((p, idx) => p.rank = idx + 1);
    return board;
};

const calculatePointsForPlayer = (prediction, currentMatchScore, oddsWin, oddsExact) => {
    const STAKE = 100;
    const {home: predH, away: predA} = prediction;
    const {team1: scoreH, team2: scoreA} = currentMatchScore;

    let totalPoints = 0;
    const diffPred = predH - predA;
    const diffScore = scoreH - scoreA;
    if (Math.sign(diffPred) === Math.sign(diffScore)) {
        totalPoints += Math.floor(STAKE * oddsWin);
    }
    if (predH === scoreH && predA === scoreA) {
        totalPoints += Math.floor(STAKE * oddsExact);
    }
    return totalPoints;
};

export default function DemoPage() {
    const {t, i18n} = useTranslation();

    const [initialConfig] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const targetLang = (params.get("lang") && DEMO_FIXTURES[params.get("lang")]) ? params.get("lang") : (DEMO_FIXTURES[i18n.language] ? i18n.language : 'fr');
        const teams = DEMO_FIXTURES[targetLang];
        const rewards = DEMO_REWARDS[targetLang];
        const finalBarName = params.get("bar") || BASE_CONTEST_DATA.place.name;
        // Mode test isolé : /demo?penalty → on entre directement dans la séance de tirs au but
        // (pas de prono FT, pas de formulaire, pas de modale mi-temps).
        const penaltyOnly = params.get("penalty") != null;
        // Mode test isolé : /demo?penalty-onboarding → tunnel d'un nouveau venu QUI ARRIVE pendant la
        // séance : welcome pénos → form (pseudo/email, sans prono) → séance.
        const penaltyOnboarding = params.get("penalty-onboarding") != null;
        const inShootout = penaltyOnly || penaltyOnboarding;

        return {
            lang: targetLang,
            penaltyOnly,
            penaltyOnboarding,
            data: {
                ...BASE_CONTEST_DATA,
                fixture: {
                    ...BASE_CONTEST_DATA.fixture,
                    state: inShootout ? FIXTURE_STATE_CONFIG.PENALTY_SHOOTOUT : BASE_CONTEST_DATA.fixture.state,
                    homeTeam: teams.home,
                    awayTeam: teams.away
                },
                participantUi: {
                    rewardLabel: rewards.rewardLabel,
                    penaltyRewardLabel: DEMO_PENALTY_REWARD.title
                },
                winnerReward: rewards.winnerReward,
                loserReward: rewards.loserReward,
                place: {
                    ...BASE_CONTEST_DATA.place,
                    name: finalBarName
                }
            }
        };
    });

    useEffect(() => {
        if (i18n.language !== initialConfig.lang) {
            i18n.changeLanguage(initialConfig.lang);
        }
    }, [initialConfig.lang, i18n]);

    const [contestData, setContestData] = useState(initialConfig.data);
    const [step, setStep] = useState(
        initialConfig.penaltyOnboarding ? DEMO_ONBOARD_WELCOME
            : initialConfig.penaltyOnly ? DEMO_PENALTY_STEP
                : APP_STEPS.GAME
    );
    const stepRef = React.useRef(step);
    useEffect(() => { stepRef.current = step; }, [step]);

    const [userScore, setUserScore] = useState({team1: 1, team2: 2});
    const [userInfo, setUserInfo] = useState({pseudo: randomPseudo, email: "test@gojokodo.fr"});
    const [isLoading, setIsLoading] = useState(false);

    // --- PARCOURS MI-TEMPS (opt-in) ---
    const [userScoreHT, setUserScoreHT] = useState({team1: 0, team2: 0});
    const [htUpsellOpen, setHtUpsellOpen] = useState(false);
    const [playHalfTime, setPlayHalfTime] = useState(false);
    const [quizDone, setQuizDone] = useState(false);
    const [quizUpsellOpen, setQuizUpsellOpen] = useState(false);
    const [halfTimeReward, setHalfTimeReward] = useState(null);
    const [showHtOverlay, setShowHtOverlay] = useState(false);
    const [htLeaderboardBackend, setHtLeaderboardBackend] = useState(null);

    // --- PARCOURS TIRS AU BUT (démo) ---
    // Le DemoPage joue le rôle du "feed" : il ouvre/résout chaque tir et alimente le composant
    // présentatiel PenaltyShootout. En prod, ces props viendront du SSE.
    const [penKick, setPenKick] = useState(null);           // {number, taker, phase, actualResult}
    const [penScore, setPenScore] = useState({home: 0, away: 0});
    const [penHistory, setPenHistory] = useState([]);
    const [penBoard, setPenBoard] = useState([]);
    const [penStreak, setPenStreak] = useState(0);
    const [penMyPrediction, setPenMyPrediction] = useState(null);
    const [penLastOutcome, setPenLastOutcome] = useState(null);

    const penStartedRef = React.useRef(false);   // garde anti double-lancement du driver
    const penMyPredRef = React.useRef(null);      // mon prono (lu par le driver à la fermeture)
    const penReactionRef = React.useRef(null);    // temps de réaction (ms) depuis l'ouverture
    const penKickStartRef = React.useRef(0);
    const penUnblockRef = React.useRef(null);     // résout l'attente du tour quand je tape

    const htLeaderboard = useMemo(() =>
            htLeaderboardBackend ? getSmartView(mapBackendToFrontend(htLeaderboardBackend)) : null,
        [htLeaderboardBackend]
    );

    // Gains potentiels mi-temps (illustratifs pour la démo : le score exact rapporte d'autant plus
    // qu'il est rare). Structure identique au final pour rester cohérent côté affichage.
    const htGains = useMemo(() => {
        const {team1, team2} = userScoreHT;
        const isDraw = team1 === team2;
        const win = isDraw ? 120 : 100;
        const score = 200 + (team1 + team2) * 80;
        return {win, score, total: win + score};
    }, [userScoreHT]);

    // 💡 ÉTAT DU CURSEUR FANTÔME
    const [ghostCursor, setGhostCursor] = useState({
        active: false,
        x: window.innerWidth / 2,
        y: window.innerHeight + 100, // Commence hors écran en bas
        clicking: false
    });

    const calculations = useMemo(() => {
        return calculatePotentialGains(userScore, MOCK_ODDS_LIST);
    }, [userScore]);

    // Aux tirs au but, le match fin de temps réglementaire est forcément un NUL → l'affiche montre 2-2
    // (sinon 1-2 + "tirs au but" serait incohérent). Sinon score live démo classique.
    const [matchScore, setMatchScore] = useState(
        (initialConfig.penaltyOnly || initialConfig.penaltyOnboarding) ? {team1: 2, team2: 2} : {team1: 1, team2: 2}
    );

    const [leaderboardBackend, setLeaderboardBackend] = useState(() =>
        makeMockFootballLeaderboardBackend(5, {
            meIndex: 4,
            startId: 30,
            targetScore: {home: 1, away: 2}
        })
    );

    const leaderboard = useMemo(() =>
            getSmartView(mapBackendToFrontend(leaderboardBackend)),
        [leaderboardBackend]
    );

    const [overlays, setOverlays] = useState({
        goal: false,
        qr: {isOpen: false, title: "", url: ""},
        selectedPlayer: null
    });

    // Démo : on rejoue le comportement de ContestPage (modale avis à l'arrivée sur le résultat),
    // mais SANS aucun tracking — la démo ne doit jamais émettre d'event.
    const [showGoogleReview, setShowGoogleReview] = useState(false);
    const [googleReviewDismissed, setGoogleReviewDismissed] = useState(false);
    useEffect(() => {
        const isResult = step === APP_STEPS.RESULT_WIN || step === APP_STEPS.RESULT_LOSE;
        if (isResult && contestData.place?.googlePlaceId && !googleReviewDismissed && !showGoogleReview) {
            setShowGoogleReview(true);
        }
    }, [step, googleReviewDismissed, showGoogleReview, contestData.place]);

    const handleScoreChange = useCallback((team, op) => {
        setUserScore((prev) => {
            const current = prev[team];
            let next = op === "plus" ? current + 1 : current - 1;
            if (next < 0) next = 0;
            return {...prev, [team]: next};
        });
    }, []);

    const processSubmission = useCallback((overrideScore = null) => {
        setIsLoading(true);
        const finalScore = overrideScore || userScore;

        setTimeout(() => {
            setLeaderboardBackend(prevList => {
                const newList = prevList.map(p => ({...p}));
                const me = newList.find(p => p.isMe);

                if (me) {
                    me.pseudo = userInfo.pseudo;
                    me.predictionHome = finalScore.team1;
                    me.predictionAway = finalScore.team2;
                    me.oddsExactScore = parseFloat(calculations.oddsScore);
                    me.oddsWinner = parseFloat(calculations.oddsWin);

                    me.totalPoints = calculatePointsForPlayer(
                        {home: finalScore.team1, away: finalScore.team2},
                        matchScore,
                        me.oddsWinner,
                        me.oddsExactScore
                    );
                }

                newList.sort((a, b) => b.totalPoints - a.totalPoints);
                newList.forEach((p, idx) => p.rank = idx + 1);
                return newList;
            });

            setIsLoading(false);
            // On atterrit sur le classement ET on ouvre l'upsell mi-temps PAR-DESSUS
            // (le joueur voit le classement live derrière la modale).
            setStep(APP_STEPS.LEADERBOARD);
            setHtUpsellOpen(true);
        }, 600);
    }, [userInfo, userScore, calculations, matchScore]);

    // Bornage : le score mi-temps ne peut pas dépasser le prono final (un but ne se perd pas).
    const handleHtScoreChange = useCallback((team, op) => {
        setUserScoreHT(prev => {
            const max = userScore[team];
            let next = op === "plus" ? prev[team] + 1 : prev[team] - 1;
            if (next < 0) next = 0;
            if (next > max) next = max;
            return {...prev, [team]: next};
        });
    }, [userScore]);

    // Après la décision mi-temps : on revient au classement et, si le quizz n'a pas encore
    // été joué, on propose la MODALE d'invitation au quizz par-dessus (pas de lancement direct).
    const goAfterHalfTime = useCallback(() => {
        setStep(APP_STEPS.LEADERBOARD);
        if (!quizDone) setQuizUpsellOpen(true);
    }, [quizDone]);

    const acceptQuiz = useCallback(() => {
        setQuizUpsellOpen(false);
        setStep(APP_STEPS.QUIZ);
    }, []);

    const declineQuiz = useCallback(() => {
        setQuizUpsellOpen(false);
        // Refus non bloquant : le bandeau de rejeu reste dispo sur le classement.
    }, []);

    const acceptHalfTime = useCallback(() => {
        setHtUpsellOpen(false);
        setUserScoreHT({team1: 0, team2: 0});
        setStep(DEMO_HT_STEPS.BETTING);
    }, []);

    // Retour depuis la saisie : on rouvre la décision (l'utilisateur peut finalement passer).
    const backFromHalfTime = useCallback(() => {
        setStep(APP_STEPS.LEADERBOARD);
        setHtUpsellOpen(true);
    }, []);

    const declineHalfTime = useCallback(() => {
        setHtUpsellOpen(false);
        setPlayHalfTime(false);
        setHalfTimeReward(null);
        // Classement mi-temps live SANS le joueur (il n'a pas joué la pause).
        setHtLeaderboardBackend(buildHalfTimeBoard({withMe: false}));
        goAfterHalfTime();
    }, [goAfterHalfTime]);

    const validateHalfTime = useCallback(() => {
        setPlayHalfTime(true);
        // Pas encore de lot : on est en 1ère mi-temps, le joueur MÈNE la course mais le
        // sifflet (et donc l'attribution du lot) n'a pas encore eu lieu.
        setHtLeaderboardBackend(buildHalfTimeBoard({withMe: true, prediction: userScoreHT, pseudo: userInfo.pseudo}));
        goAfterHalfTime();
    }, [userScoreHT, userInfo.pseudo, goAfterHalfTime]);

    const handleQuizComplete = useCallback((answers) => {
        let pts = 0;
        answers.forEach(({questionId, chosenChoiceId, timeTakenMs}) => {
            const q = MOCK_QUIZ.questions.find(x => x.id === questionId);
            if (q && chosenChoiceId === q._correctChoiceId) {
                pts += computeQuizPoints(q.basePoints, q.timeLimitSec, timeTakenMs);
            }
        });
        // Le bonus quizz s'ajoute aux points du joueur sur le classement final.
        setLeaderboardBackend(prev => {
            const list = prev.map(p => ({...p}));
            const me = list.find(p => p.isMe);
            if (me) {
                me.quizBonus = pts;
                me.totalPoints = (me.totalPoints || 0) + pts;
            }
            list.sort((a, b) => b.totalPoints - a.totalPoints);
            list.forEach((p, idx) => p.rank = idx + 1);
            return list;
        });
        setQuizDone(true);
        setStep(APP_STEPS.LEADERBOARD);
    }, []);

    // --- DRIVER TIRS AU BUT (simule le feed Sportmonks tir-par-tir) ---

    // Tap sur BUT / RATÉ : enregistre mon prono + mon temps de réaction, puis débloque le tour.
    const onPenaltyPredict = useCallback((pred) => {
        if (penMyPredRef.current != null) return;
        penMyPredRef.current = pred;
        penReactionRef.current = Date.now() - penKickStartRef.current;
        setPenMyPrediction(pred);
        if (penUnblockRef.current) penUnblockRef.current();
    }, []);

    const runPenaltyShootout = useCallback(async () => {
        const wait = (ms) => new Promise(r => setTimeout(r, ms));
        // Attend MON tap (révèle ~1s après pour le suspense) OU l'expiration de la fenêtre.
        const waitWindow = () => new Promise(resolve => {
            let done = false;
            const finish = () => { if (done) return; done = true; clearTimeout(to); penUnblockRef.current = null; resolve(); };
            const to = setTimeout(finish, PEN_WINDOW_MS);
            penUnblockRef.current = () => setTimeout(finish, PEN_TAP_REVEAL_MS);
        });

        let score = {home: 0, away: 0};
        let history = [];
        let streak = 0;
        let myTotal = 0;
        // Mini-classement : 4 adversaires (cadence de gain variable) + moi.
        let board = [
            ...[0, 1, 2, 3].map(i => ({
                id: 'pen-opp-' + i, pseudo: generatePseudo(), points: 0, isMe: false,
                rate: 28 + Math.round(Math.random() * 36), penaltyKicks: []
            })),
            {id: 'pen-me', pseudo: userInfo.pseudo, points: 0, isMe: true, penaltyKicks: []}
        ];
        setPenScore(score); setPenHistory(history); setPenStreak(0); setPenBoard(board); setPenLastOutcome(null);

        for (let i = 0; i < DEMO_PENALTY_SCRIPT.length; i++) {
            const k = DEMO_PENALTY_SCRIPT[i];

            // 1) Ouverture du tir
            penMyPredRef.current = null;
            penReactionRef.current = null;
            penKickStartRef.current = Date.now();
            setPenMyPrediction(null);
            setPenLastOutcome(null);
            // Le 1er tir s'ouvre en UNKNOWN (on ignore qui tire en premier tant que le feed n'a pas tranché)
            // → médaillon neutre "Premier tireur" ; le vrai tireur est révélé au REVEAL. Les tirs suivants
            // s'ouvrent avec leur tireur (alternance dérivée du 1er).
            const openTaker = i === 0 ? 'UNKNOWN' : k.taker;
            setPenKick({number: i + 1, taker: openTaker, takerName: null, phase: 'OPEN'});

            await waitWindow();

            // 2) Résolution de mon prono (scoring vite + juste + série)
            const myPred = penMyPredRef.current;
            const correct = myPred != null && myPred === k.result;
            let points = 0;
            if (correct) {
                const reaction = penReactionRef.current ?? PEN_WINDOW_MS;
                const remainFrac = Math.max(0, 1 - reaction / PEN_WINDOW_MS);
                streak += 1;
                points = Math.round(100 * (0.4 + 0.6 * remainFrac) * (1 + 0.25 * (streak - 1)));
            } else {
                streak = 0;
            }
            myTotal += points;

            // 3) Score de la séance + historique
            if (k.result === 'GOAL') score = k.taker === 'HOME'
                ? {...score, home: score.home + 1}
                : {...score, away: score.away + 1};
            history = [...history, {taker: k.taker, result: k.result}];

            // 4) Mini-classement : les adversaires marquent aussi → le board se réordonne.
            //    Pastilles par tir : moi = mon prono (CORRECT/WRONG/ABSENT) ; adversaires = simulé
            //    (probabilité de juste ~ leur cadence, parfois absent).
            const myKickResult = myPred == null ? 'ABSENT' : (correct ? 'CORRECT' : 'WRONG');
            board = board.map(p => {
                if (p.isMe) {
                    return {...p, points: myTotal, penaltyKicks: [...p.penaltyKicks, myKickResult]};
                }
                const oppResult = Math.random() < 0.10
                    ? 'ABSENT'
                    : (Math.random() < (p.rate / 100 + 0.2) ? 'CORRECT' : 'WRONG');
                return {
                    ...p,
                    points: p.points + Math.round(p.rate * (0.5 + Math.random())),
                    penaltyKicks: [...p.penaltyKicks, oppResult]
                };
            }).sort((a, b) => b.points - a.points);

            // 5) Reveal
            setPenKick({number: i + 1, taker: k.taker, takerName: null, phase: 'REVEAL', actualResult: k.result});
            setPenLastOutcome({correct, points, streak});
            setPenScore(score); setPenHistory(history); setPenStreak(streak); setPenBoard(board);

            await wait(PEN_REVEAL_MS);
        }

        // Fin de séance : pour la démo, on garantit la 1ère place (showcase du lot pénos).
        const maxOpp = Math.max(...board.filter(p => !p.isMe).map(p => p.points), 0);
        board = board.map(p => p.isMe ? {...p, points: Math.max(p.points, maxOpp + 60)} : p)
                     .sort((a, b) => b.points - a.points);
        setPenBoard(board);

        await wait(500);
        // Latecomer : écran résultat DÉDIÉ tirs au but (StepPenaltyResult), pas le StepResult FT.
        setStep(DEMO_PENALTY_RESULT);
    }, [userInfo.pseudo]);

    // Lance le driver à l'entrée dans le mode pénos (une seule fois).
    // Différé d'un tick : évite tout setState synchrone dans l'effet.
    // PAS de clearTimeout au cleanup : sous StrictMode (dev), l'effet est monté→nettoyé→remonté ;
    // annuler le timeout tuerait le seul lancement et le garde penStartedRef bloquerait la 2e
    // tentative → la séance ne démarrerait jamais. Le garde suffit à empêcher un double départ.
    useEffect(() => {
        if (step !== DEMO_PENALTY_STEP || penStartedRef.current) return;
        penStartedRef.current = true;
        setTimeout(() => runPenaltyShootout(), 0);
    }, [step, runPenaltyShootout]);

    const handleStealthAction = useCallback(() => {
        const fixtureState = contestData.fixture.state;

        // ÉTAPE 1 : coup de sifflet de la mi-temps (depuis la 1ère période).
        // Le classement Mi-temps se fige, le Final devient le classement live.
        if (fixtureState === FIXTURE_STATE_CONFIG.FIRST_HALF || fixtureState === FIXTURE_STATE_CONFIG.HALF_TIME) {
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
            setContestData(prev => ({
                ...prev,
                fixture: {...prev.fixture, state: FIXTURE_STATE_CONFIG.SECOND_HALF, minutes: 46}
            }));
            // Le joueur qui a joué la mi-temps gagne le lot → célébration.
            if (playHalfTime) {
                setHalfTimeReward(DEMO_HALFTIME_REWARD);
                setShowHtOverlay(true);
            }
            return;
        }

        // ÉTAPE 2 : but en 2e période, puis fin de match (logique existante).
        if (matchScore.team1 === 1) {
            setOverlays(prev => ({...prev, goal: true}));
            if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 500]);

            setTimeout(() => {
                const newScore = {team1: 2, team2: 2};
                setMatchScore(newScore);

                setContestData(prev => ({
                    ...prev,
                    fixture: {
                        ...prev.fixture,
                        homeTeam: {...prev.fixture.homeTeam, score: 2},
                        awayTeam: {...prev.fixture.awayTeam, score: 2}
                    }
                }));

                setLeaderboardBackend(prevList => {
                    const newList = prevList.map(p => {
                        const newPoints = calculatePointsForPlayer(
                            {home: p.predictionHome, away: p.predictionAway},
                            newScore,
                            p.oddsWinner,
                            p.oddsExactScore
                        );
                        return {...p, totalPoints: newPoints};
                    });

                    newList.sort((a, b) => b.totalPoints - a.totalPoints);
                    newList.forEach((p, idx) => p.rank = idx + 1);
                    return newList;
                });

                setTimeout(() => setOverlays(prev => ({...prev, goal: false})), 4000);
            }, 500);
        } else if (matchScore.team1 === 2) {
            if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
            // Match nul 2-2 en knockout → séance de tirs au but (mode plein écran auto, pour tous).
            setContestData(prev => ({
                ...prev,
                fixture: {...prev.fixture, state: FIXTURE_STATE_CONFIG.PENALTY_SHOOTOUT}
            }));
            setStep(DEMO_PENALTY_STEP);
        }
    }, [contestData.fixture.state, matchScore, playHalfTime]);

    const resetDemo = () => {
        setStep(APP_STEPS.GAME);
        setShowGoogleReview(false);
        setGoogleReviewDismissed(false);
        setContestData(initialConfig.data);
        setUserScore({team1: 1, team2: 2});
        setMatchScore({team1: 1, team2: 2});
        setUserInfo({pseudo: generatePseudo(), email: "test@gojokodo.fr"});
        setLeaderboardBackend(makeMockFootballLeaderboardBackend(5, {
            meIndex: 4, startId: 30, targetScore: {home: 1, away: 2}
        }));
        setUserScoreHT({team1: 0, team2: 0});
        setHtUpsellOpen(false);
        setPlayHalfTime(false);
        setQuizDone(false);
        setQuizUpsellOpen(false);
        setHalfTimeReward(null);
        setShowHtOverlay(false);
        setHtLeaderboardBackend(null);
        // Pénos
        setPenKick(null);
        setPenScore({home: 0, away: 0});
        setPenHistory([]);
        setPenBoard([]);
        setPenStreak(0);
        setPenMyPrediction(null);
        setPenLastOutcome(null);
        penStartedRef.current = false;
    };

    // 💡 SÉQUENCE DE CLICS AUTOMATISÉE
    useEffect(() => {
        const simulateClick = (elementId, actionCallback, delayBefore, fallbackX, fallbackY) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    const el = document.getElementById(elementId);
                    let targetX = fallbackX;
                    let targetY = fallbackY;

                    if (el) {
                        const rect = el.getBoundingClientRect();
                        targetX = rect.left + rect.width / 2;
                        targetY = rect.top + rect.height / 2;
                    }

                    setGhostCursor(prev => ({...prev, x: targetX, y: targetY}));

                    setTimeout(() => {
                        setGhostCursor(prev => ({...prev, clicking: true}));
                        setTimeout(() => {
                            setGhostCursor(prev => ({...prev, clicking: false}));
                            actionCallback();
                            resolve();
                        }, 200);
                    }, 600);
                }, delayBefore);
            });
        };

        const handleMessage = async (event) => {
            // Garde : pendant la séance de tirs au but (et en mode test isolé /demo?penalty), on
            // IGNORE les messages scriptés de la landing parente — sinon un SIMULATE_WIN sur timer
            // fixe éjecterait le joueur vers le résultat avant la fin des pénos. RESET reste permis.
            const inPenalty = stepRef.current === DEMO_PENALTY_STEP || initialConfig.penaltyOnly || initialConfig.penaltyOnboarding;
            if (inPenalty && event.data.type !== 'RESET_DEMO') return;

            // PARTIE 1 : Le doigt clique sur "+" et "Valider" (s'arrête au formulaire)
            if (event.data.type === 'SIMULATE_BET_PART1') {
                setGhostCursor({active: true, x: window.innerWidth / 2, y: window.innerHeight + 100, clicking: false});

                await simulateClick('demo-btn-plus', () => handleScoreChange('team1', 'plus'), 300, window.innerWidth / 2 - 50, 300);
                await simulateClick('demo-btn-validate', () => setStep(APP_STEPS.FORM), 800, window.innerWidth / 2, 550);

                setTimeout(() => setGhostCursor(prev => ({...prev, active: false})), 500);
            }

            // PARTIE 2 : Le doigt clique sur "Jouer" (valide le formulaire)
            if (event.data.type === 'SIMULATE_BET_PART2') {
                setGhostCursor({active: true, x: window.innerWidth / 2, y: window.innerHeight + 100, clicking: false});

                await simulateClick('demo-btn-submit', () => processSubmission({
                    team1: 2,
                    team2: 2
                }), 400, window.innerWidth / 2, 550);

                setTimeout(() => setGhostCursor(prev => ({...prev, active: false})), 500);
            }

            if (event.data.type === 'SIMULATE_GOAL') {
                handleStealthAction();
            }

            if (event.data.type === 'SIMULATE_WIN') {
                setStep(APP_STEPS.RESULT_WIN);
            }

            // RESET COMPLET (depuis le bouton de la Landing Page)
            if (event.data.type === 'RESET_DEMO') {
                setGhostCursor(prev => ({...prev, active: false}));
                resetDemo();
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [handleScoreChange, processSubmission, handleStealthAction]);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        processSubmission();
    };

    const updateOverlay = (key, value) => {
        setOverlays(prev => ({...prev, [key]: value}));
    };

    const handleShowQr = (reward) => {
        const url = reward.code || "DEMO-CODE";
        updateOverlay('qr', {
            isOpen: true,
            title: reward.title,
            url: url
        });
    };

    const renderContent = () => {
        if (step === APP_STEPS.GAME) {
            return (
                <StepBettingFootball
                    userScore={userScore}
                    matchScore={matchScore}
                    handleScoreChange={handleScoreChange}
                    calculations={calculations}
                    onValidate={() => setStep(APP_STEPS.FORM)}
                    isDemo={true}
                />
            );
        }

        if (step === APP_STEPS.FORM) {
            return (
                <StepForm
                    userInfo={userInfo}
                    setUserInfo={setUserInfo}
                    handleFormSubmit={handleFormSubmit}
                    userScore={userScore}
                    isLoading={isLoading}
                    apiError={null}
                    onBack={() => setStep(APP_STEPS.GAME)}
                    isDemo={true} // 💡 LE BYPASS DU CAPTCHA EST ICI
                />
            );
        }

        if (step === DEMO_HT_STEPS.BETTING) {
            return (
                <StepBettingHalfTime
                    htScore={userScoreHT}
                    finalScore={userScore}
                    homeTeam={contestData.fixture.homeTeam}
                    awayTeam={contestData.fixture.awayTeam}
                    onScoreChange={handleHtScoreChange}
                    onValidate={validateHalfTime}
                    onBack={backFromHalfTime}
                    gains={htGains}
                />
            );
        }

        if (step === APP_STEPS.QUIZ) {
            return <QuizzGame questions={MOCK_QUIZ.questions} onComplete={handleQuizComplete}/>;
        }

        if (step === APP_STEPS.LEADERBOARD) {
            const fixtureState = contestData.fixture.state;
            const currentPhase = (fixtureState === FIXTURE_STATE_CONFIG.FIRST_HALF || fixtureState === FIXTURE_STATE_CONFIG.HALF_TIME)
                ? 'HALF_TIME' : 'FINAL';
            const htWinnerPseudo = playHalfTime
                ? userInfo.pseudo
                : (htLeaderboard?.find(r => !r.isSeparator)?.pseudo ?? null);
            return (
                <div className="relative">
                    <div
                        className="absolute -top-32 left-0 w-full h-24 z-50 cursor-pointer"
                        onClick={handleStealthAction}
                    />
                    <LeaderboardTabs
                        finalLeaderboard={leaderboard}
                        halfTimeLeaderboard={htLeaderboard}
                        currentPhase={currentPhase}
                        played={playHalfTime}
                        halfTimeWinnerPseudo={htWinnerPseudo}
                        halfTimeReward={halfTimeReward}
                        onShowHalfTimeReward={() => setShowHtOverlay(true)}
                        canPlayHalfTime={!playHalfTime && currentPhase === 'HALF_TIME'}
                        onPlayHalfTime={acceptHalfTime}
                        canPlayQuiz={!quizDone}
                        onPlayQuiz={acceptQuiz}
                        isGoalFlashing={overlays.goal}
                        setSelectedPlayer={(player) => updateOverlay('selectedPlayer', player)}
                    />
                </div>
            );
        }

        if (step === DEMO_ONBOARD_WELCOME) {
            return (
                <StepWelcomePenalty
                    contestData={contestData}
                    onStart={() => setStep(DEMO_ONBOARD_FORM)}
                />
            );
        }

        if (step === DEMO_ONBOARD_FORM) {
            return (
                <StepForm
                    userInfo={userInfo}
                    setUserInfo={setUserInfo}
                    handleFormSubmit={() => setStep(DEMO_PENALTY_STEP)}
                    isLoading={false}
                    apiError={null}
                    title={t('penalty.form_title', 'Plus que ton pseudo')}
                    description={t('penalty.form_subtitle', 'Ton pseudo + email pour jouer les tirs au but.')}
                    icon="⚽"
                    backLabel={t('penalty.form_back', 'Retour')}
                    onBack={() => setStep(DEMO_ONBOARD_WELCOME)}
                    isDemo={true}
                />
            );
        }

        if (step === DEMO_PENALTY_STEP) {
            return (
                <PenaltyShootout
                    homeTeam={contestData.fixture.homeTeam}
                    awayTeam={contestData.fixture.awayTeam}
                    kickNumber={penKick?.number ?? 1}
                    taker={penKick?.taker ?? 'HOME'}
                    phase={penKick?.phase ?? 'OPEN'}
                    actualResult={penKick?.actualResult}
                    myPrediction={penMyPrediction}
                    onPredict={onPenaltyPredict}
                    lastOutcome={penLastOutcome}
                    score={penScore}
                    history={penHistory}
                    board={penBoard}
                    myStreak={penStreak}
                />
            );
        }

        if (step === DEMO_PENALTY_RESULT) {
            // Latecomer péno-only : écran résultat dédié (la démo garantit la 1re place → WIN + lot péno).
            return (
                <div onClick={resetDemo} className="cursor-pointer min-h-full px-5">
                    <StepPenaltyResult
                        status="WIN"
                        reward={DEMO_PENALTY_REWARD}
                        rank={1}
                        place={contestData.place}
                        onShowQr={handleShowQr}
                        isPreview={true}
                    />
                </div>
            );
        }

        if (step === APP_STEPS.RESULT_WIN || step === APP_STEPS.RESULT_LOSE) {
            const r = step === APP_STEPS.RESULT_WIN ? contestData.winnerReward : contestData.loserReward;
            return (
                <div onClick={resetDemo} className="cursor-pointer min-h-full">
                    <StepResult
                        contestId={null}
                        place={contestData.place}
                        status={step}
                        reward={r}
                        pseudo={userInfo.pseudo}
                        triggerAnimation={step === APP_STEPS.RESULT_WIN}
                        onShowQr={handleShowQr}
                        isPreview={true}
                    />
                </div>
            );
        }

        return <div className="p-4 pt-10 text-center">Fin de la démo.</div>;
    };

    // Le flow tirs au but (séance ou onboarding latecomer) n'est PAS concerné par les modales
    // classement/mi-temps/quizz : en prod ContestPage retourne tôt sur la branche pénos, donc elles ne
    // sont jamais rendues — on reproduit ça ici (la démo rend toutes les modales en bas).
    const inPenaltyFlow = step === DEMO_PENALTY_STEP || step === DEMO_ONBOARD_WELCOME || step === DEMO_ONBOARD_FORM;

    return (
        <div className="h-screen overflow-y-auto font-sans bg-slate-50 text-gray-800 relative overflow-x-hidden">
            <style>{`
                .scroll-smooth { scroll-behavior: smooth; }
                .clip-image { mask-image: linear-gradient(to bottom, black 50%, transparent 100%); -webkit-mask-image: linear-gradient(to bottom, black 50%, transparent 100%); }
                .rendering-pixelated { image-rendering: pixelated; }
                @keyframes popInFlex { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
                .animate-pop-in-flex { animation: popInFlex 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
            `}</style>

            <div className="max-w-[440px] mx-auto min-h-full relative shadow-2xl bg-white/50">
                {(() => {
                    const stepsWithLayout = [APP_STEPS.GAME, APP_STEPS.FORM, APP_STEPS.LEADERBOARD, APP_STEPS.MISSED, DEMO_HT_STEPS.BETTING, DEMO_ONBOARD_FORM];
                    if (stepsWithLayout.includes(step)) {
                        return (
                            <GameLayout
                                contestData={contestData}
                                matchScore={matchScore}
                                isGoalFlashing={overlays.goal}
                            >
                                {renderContent()}
                            </GameLayout>
                        );
                    }
                    return renderContent();
                })()}

                <Footer/>
                <QrCodeOverlay isOpen={overlays.qr.isOpen} title={overlays.qr.title} url={overlays.qr.url}
                               onClose={() => updateOverlay('qr', {...overlays.qr, isOpen: false})}/>
                <GoogleReviewOverlay
                    isOpen={showGoogleReview}
                    placeName={contestData.place?.name}
                    onReview={() => {
                        // Démo : pas de tracking, on ouvre simplement la vraie fiche Google
                        window.open(`https://search.google.com/local/writereview?placeid=${contestData.place.googlePlaceId}`, '_blank', 'noopener,noreferrer');
                        setShowGoogleReview(false);
                        setGoogleReviewDismissed(true);
                    }}
                    onClose={() => { setShowGoogleReview(false); setGoogleReviewDismissed(true); }}
                />
                {overlays.selectedPlayer && (
                    <ModalPlayerDetail player={overlays.selectedPlayer}
                                       fixtureStartingAt={contestData.fixture.startingAt}
                                       onClose={() => updateOverlay('selectedPlayer', null)}/>
                )}

                {/* Upsell mi-temps : par-dessus le classement, après prono final + inscription.
                    Jamais pendant le flow tirs au but. */}
                <HalfTimeUpsellModal
                    isOpen={htUpsellOpen && !inPenaltyFlow}
                    rewardLabel={DEMO_HALFTIME_REWARD.title}
                    onAccept={acceptHalfTime}
                    onDecline={declineHalfTime}
                />

                {/* Invitation au quizz : par-dessus le classement, après le parcours mi-temps.
                    Jamais pendant le flow tirs au but. */}
                <QuizUpsellModal
                    isOpen={quizUpsellOpen && !inPenaltyFlow}
                    onAccept={acceptQuiz}
                    onDecline={declineQuiz}
                />

                {/* Célébration : lot gagné à la mi-temps (QR code). Jamais pendant le flow tirs au but. */}
                <HalfTimeRewardOverlay
                    isOpen={showHtOverlay && !inPenaltyFlow}
                    reward={halfTimeReward}
                    onClose={() => setShowHtOverlay(false)}
                />

            </div>

            {/* 💡 LE CURSEUR FANTÔME ANIMÉ */}
            <AnimatePresence>
                {ghostCursor.active && (
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{
                            opacity: 1,
                            x: ghostCursor.x - 24, // -24 pour centrer le cercle
                            y: ghostCursor.y - 24,
                            scale: ghostCursor.clicking ? 0.8 : 1 // Effet d'appui "enfoncé"
                        }}
                        exit={{opacity: 0}}
                        transition={{
                            x: {type: "spring", stiffness: 60, damping: 15},
                            y: {type: "spring", stiffness: 60, damping: 15},
                            scale: {duration: 0.15}
                        }}
                        className="fixed top-0 left-0 z-[9999] pointer-events-none w-12 h-12 bg-white/50 border-2 border-white rounded-full shadow-[0_0_20px_rgba(0,0,0,0.3)] backdrop-blur-sm flex items-center justify-center"
                    >
                        <div
                            className={`w-4 h-4 rounded-full bg-action-green transition-opacity duration-200 ${ghostCursor.clicking ? 'opacity-100 shadow-[0_0_10px_#10B981]' : 'opacity-0'}`}/>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}